import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  PageHeader,
  Row,
  Spin,
  Switch,
  Tag,
  Tooltip,
  message,
  Table,
} from "antd";
import {
  DownloadOutlined,
  FilePdfOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

import ButtonItem from "../../../Components/ButtonItem";
import SelectEmpleadosItem from "../../../Components/SelectEmpleadosItem";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import {
  construirHojaTabla,
  descargarLibro,
  limpiarNombreArchivo,
  texto,
  centro,
  fecha,
} from "../../../Components/ExcelTablaReporte";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
moment.locale("es");

const { RangePicker } = DatePicker;

// Mapa de colores: lo que manda el backend → color CSS
const COLOR_MAP = {
  rojo:  "#d32f2f",
  verde: "#388e3c",
  gris:  "#9e9e9e",
};

class TotalAsistencia extends Component {
  state = {
    departamentosIds: [],
    departamentoNombre: "",
    empleadosIds: [],
    empleadoNombre: "",
    todosEmpleados: false,
    rango: moment().isoWeekday() === 1
      ? [moment().subtract(1, "week").startOf("isoWeek"), moment().subtract(1, "week").endOf("isoWeek")]
      : [moment().startOf("isoWeek"), moment()],
    mostrarDiasSinHorario: false,
    loading: false,
    rows: [],
  };

  componentDidMount() {
    this.consultar();
  }

  // =========================
  // Helpers
  // =========================
  fmtFecha = (x) => {
    if (!x) return "—";
    return moment(x).isValid() ? moment(x).format("DD/MM/YYYY") : "—";
  };

  fmtDiaSemana = (fecha) => {
    if (!fecha) return "—";
    return moment(fecha).isValid() ? moment(fecha).format("dddd") : "—";
  };

  fmtHora = (x) => {
    if (!x) return "—";
    return moment(x).isValid() ? moment(x).local().format("HH:mm") : "—";
  };

  fmtMin = (min) => {
    if (min === null || min === undefined) return "—";
    const n = Number(min);
    if (Number.isNaN(n)) return "—";
    const sign = n < 0 ? "-" : "";
    const abs = Math.abs(Math.round(n));
    const hh = Math.floor(abs / 60);
    const mm = abs % 60;
    return `${sign}${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };

  disabledDate = (current) => {
    if (current && current.isAfter(moment().endOf("day"))) return true;
    const { rango } = this.state;
    const start = rango?.[0];
    if (start) {
      const max = moment(start).add(30, "days").endOf("day");
      return current && current.isAfter(max);
    }
    return false;
  };

  // =========================
  // Render entrada/salida con color del backend
  // =========================
  renderHoraConColor = (horaDate, color) => {
    if (!horaDate) return <span style={{ color: COLOR_MAP.gris }}>—</span>;
    const hora = this.fmtHora(horaDate);
    return (
      <span style={{ color: COLOR_MAP[color] || COLOR_MAP.gris, fontWeight: color === "gris" ? 400 : 600 }}>
        {hora}
      </span>
    );
  };

  // =========================
  // API
  // =========================
  consultar = async () => {
    const { empresaId, headersToken } = this.props;
    const { rango, departamentosIds, empleadosIds, todosEmpleados, mostrarDiasSinHorario } = this.state;

    if (!rango?.[0] || !rango?.[1]) {
      message.warning("Selecciona un rango de fechas.");
      return;
    }
    if (!todosEmpleados && !departamentosIds?.length && !empleadosIds?.length) {
      message.warning("Selecciona al menos un departamento o empleado, o marca Todos los empleados.");
      return;
    }

    this.setState({ loading: true });

    try {
      const res = await axios.post(
        "reportesAsistencia/reporteTotalAsistencia",
        {
          empresaId,
          departamentosIds: departamentosIds || [],
          empleadosIds: empleadosIds || [],
          todosEmpleados,
          fechaInicio: rango[0].format("YYYY-MM-DD"),
          fechaFin: rango[1].format("YYYY-MM-DD"),
          mostrarDiasSinHorario,
        },
        { headers: headersToken }
      );

      if (res.data?.success !== 2) {
        message.error(res.data?.message || "Error al consultar el reporte.");
        this.setState({ rows: [] });
        return;
      }

      this.setState({ rows: Array.isArray(res.data?.data) ? res.data.data : [] });
    } catch (e) {
      message.error("No se pudo cargar el reporte.");
      this.setState({ rows: [] });
    } finally {
      this.setState({ loading: false });
    }
  };

  // =========================
  // Events
  // =========================
  onChangeDepartamento = (value, option) => {
    this.setState(
      {
        departamentosIds: value || [],
        departamentoNombre: option?.label || option?.children || "",
        empleadosIds: [],
        empleadoNombre: "",
      },
      () => this.consultar()
    );
  };

  onChangeEmpleado = (value, option) => {
    this.setState(
      { empleadosIds: value || [], empleadoNombre: option?.label || option?.children || "" },
      () => this.consultar()
    );
  };

  onChangeTodosEmpleados = (e) => {
    const todosEmpleados = e.target.checked;
    // Al marcarlo el reporte sale de todos, asi que los filtros de departamento
    // y empleado se limpian para que no se contradigan con el check
    this.setState(
      {
        todosEmpleados,
        departamentosIds: [],
        departamentoNombre: "",
        empleadosIds: [],
        empleadoNombre: "",
      },
      // Al desmarcar ya no queda filtro, no tiene sentido consultar (solo avisaria)
      () => { if (todosEmpleados) this.consultar(); }
    );
  };

  onChangeRango = (value) => {
    this.setState({ rango: value }, () => {
      if (value?.[0] && value?.[1]) this.consultar();
    });
  };

  onChangeMostrarSinHorario = (checked) => {
    this.setState({ mostrarDiasSinHorario: checked }, () => this.consultar());
  };

  // =========================
  // EXPORT: Excel
  // =========================
  exportExcel = () => {
    const { rows, rango, departamentoNombre, empleadoNombre } = this.state;
    if (!rows?.length) { message.warning("No hay datos para exportar."); return; }

    const columnas = [
      { titulo: "Nombre", ancho: 30 },
      { titulo: "Departamento", ancho: 20 },
      { titulo: "Fecha", ancho: 12 },
      { titulo: "Día", ancho: 12 },
      { titulo: "Excepción", ancho: 16 },
      { titulo: "Horario", ancho: 28 },
      { titulo: "Ent(H)", ancho: 9 },
      { titulo: "Sal(H)", ancho: 9 },
      { titulo: "Duración", ancho: 10 },
      { titulo: "Entrada Real", ancho: 12 },
      { titulo: "Salida Real", ancho: 12 },
      { titulo: "Tiempo Total", ancho: 12 },
      { titulo: "Jornada", ancho: 10 },
      { titulo: "Sin Asignar", ancho: 11 },
      { titulo: "Retardo", ancho: 10 },
      { titulo: "Salida Temprana", ancho: 14 },
      { titulo: "Ausencia", ancho: 11 },
      { titulo: "Extra Normal", ancho: 12 },
      { titulo: "Extra Fin Semana", ancho: 15 },
      { titulo: "Extra Festivo", ancho: 12 },
      { titulo: "Extra Descanso", ancho: 14 },
      { titulo: "Descanso Esperado", ancho: 16 },
      { titulo: "Descanso Real", ancho: 13 },
      { titulo: "Diferencia Descanso", ancho: 17 },
    ];

    const filas = rows.map((r) => [
      texto(r.nombre),
      texto(r.departamento),
      r.fecha ? fecha(r.fecha) : centro("—"),
      texto(this.fmtDiaSemana(r.fecha)),
      texto(r.excepcion ?? "—"),
      texto((r.horarioTexto || "—").toString().replace(/\n/g, " | ")),
      centro(r.horaEntrada || "—"),
      centro(r.horaSalida || "—"),
      centro(this.fmtMin(r.duracionMin)),
      centro(this.fmtHora(r.entrada)),
      centro(this.fmtHora(r.salida)),
      centro(this.fmtMin(r.tiempoTotalMin)),
      centro(this.fmtMin(r.tiempoJornadaMin)),
      centro(this.fmtMin(r.sinAsignarMin)),
      centro(this.fmtMin(r.retardoMin)),
      centro(this.fmtMin(r.salidaTempranaMin)),
      centro(r.excepcion === "Vacaciones" ? "Vacaciones" : r.ausencia ? "Sí" : "No"),
      centro(this.fmtMin(r.extraNormalMin)),
      centro(this.fmtMin(r.extraFinSemanaMin)),
      centro(this.fmtMin(r.extraFestivoMin)),
      centro(this.fmtMin(r.extraDescansoMin)),
      centro(this.fmtMin(r.descansoEsperadoMin)),
      centro(this.fmtMin(r.descansoRealMin)),
      centro(this.fmtMin(r.descansoDiferenciaMin)),
    ]);

    const descripcionFiltros = [
      `Departamento: ${departamentoNombre || "Todos"}`,
      `Empleado: ${empleadoNombre || "Todos"}`,
      `Del ${rango?.[0]?.format("DD/MM/YYYY")} al ${rango?.[1]?.format("DD/MM/YYYY")}`,
    ].join(" · ");

    descargarLibro(
      [
        {
          nombre: "Total de Asistencia",
          hoja: construirHojaTabla({ columnas, filas, totales: [] }, {
            titulo: "Total de Asistencia",
            empresaNombre: this.props.empresa,
            descripcionFiltros,
          }),
        },
      ],
      limpiarNombreArchivo(`total_asistencia_${rango[0].format("YYYYMMDD")}_${rango[1].format("YYYYMMDD")}`)
    );
  };

  // =========================
  // EXPORT: PDF
  // =========================
  exportPDF = () => {
    const { rows, rango, departamentoNombre, empleadoNombre } = this.state;
    if (!rows?.length) { message.warning("No hay datos para exportar."); return; }

    const header = (t) => ({ text: t, bold: true, fontSize: 7 });

    const body = [[
      header("Nombre"), header("Depto"), header("Fecha"), header("Día"),
      header("Excepción"), header("Horario"), header("Ent(H)"), header("Sal(H)"),
      header("Duración"), header("Entrada"), header("Salida"), header("Total"),
      header("Jornada"), header("Sin Asig."), header("Retardo"), header("Sal.Temp."),
      header("Aus."), header("Extra N"), header("Extra FS"), header("Extra Fest"),
      header("Extra Desc"), header("Desc.Esp."), header("Desc.Real"), header("Dif.Desc"),
    ]];

    rows.forEach((r) => {
      body.push([
        r.nombre || "—", r.departamento || "—",
        this.fmtFecha(r.fecha), this.fmtDiaSemana(r.fecha),
        r.excepcion || "—", (r.horarioTexto || "—").toString(),
        r.horaEntrada || "—", r.horaSalida || "—",
        this.fmtMin(r.duracionMin),
        this.fmtHora(r.entrada), this.fmtHora(r.salida),
        this.fmtMin(r.tiempoTotalMin), this.fmtMin(r.tiempoJornadaMin),
        this.fmtMin(r.sinAsignarMin), this.fmtMin(r.retardoMin),
        this.fmtMin(r.salidaTempranaMin),
        r.excepcion === "Vacaciones" ? "Vacaciones" : r.ausencia ? "Sí" : "No",
        this.fmtMin(r.extraNormalMin), this.fmtMin(r.extraFinSemanaMin),
        this.fmtMin(r.extraFestivoMin), this.fmtMin(r.extraDescansoMin),
        this.fmtMin(r.descansoEsperadoMin), this.fmtMin(r.descansoRealMin),
        this.fmtMin(r.descansoDiferenciaMin),
      ]);
    });

    pdfMake.createPdf({
      pageSize: "A3",
      pageOrientation: "landscape",
      pageMargins: [18, 40, 18, 26],
      content: [
        { text: "Reporte: Total de Asistencia", style: "title" },
        {
          columns: [
            {
              width: "*",
              text: [
                { text: "Departamento: ", bold: true }, departamentoNombre || "Todos", "\n",
                { text: "Empleado: ", bold: true }, empleadoNombre || "Todos",
              ],
            },
            {
              width: "auto",
              text: [
                { text: "Rango: ", bold: true },
                `${rango?.[0]?.format("DD/MM/YYYY")} - ${rango?.[1]?.format("DD/MM/YYYY")}`,
              ],
            },
          ],
          margin: [0, 6, 0, 10],
        },
        { table: { headerRows: 1, body }, layout: "lightHorizontalLines" },
        { text: `Generado: ${moment().format("DD/MM/YYYY HH:mm")}`, style: "footer", margin: [0, 8, 0, 0] },
      ],
      styles: {
        title: { fontSize: 13, bold: true },
        footer: { fontSize: 9, color: "#666666" },
      },
      defaultStyle: { fontSize: 7 },
    }).download(`total_asistencia_${rango[0].format("YYYYMMDD")}_${rango[1].format("YYYYMMDD")}.pdf`);
  };

  // =========================
  // Columnas de la tabla
  // =========================
  getColumns = () => [
    {
      title: "Nombre", dataIndex: "nombre", key: "nombre",
      width: 180, ellipsis: true, fixed: "left",
      sorter: (a, b) => (a.nombre || "").localeCompare(b.nombre || ""),
    },
    {
      title: "Departamento", dataIndex: "departamento", key: "departamento",
      width: 150, ellipsis: true, fixed: "left",
      sorter: (a, b) => (a.departamento || "").localeCompare(b.departamento || ""),
    },
    {
      title: "Fecha", dataIndex: "fecha", key: "fecha", width: 110,
      render: (v) => this.fmtFecha(v),
      sorter: (a, b) => moment(a.fecha).valueOf() - moment(b.fecha).valueOf(),
      defaultSortOrder: "ascend",
    },
    {
      title: "Día", key: "diaSemana", width: 110,
      render: (_, r) => this.fmtDiaSemana(r.fecha),
    },
    {
      title: "Excepción", dataIndex: "excepcion", key: "excepcion", width: 140,
      render: (v) => v
        ? <Tag color={v === "Día de descanso" ? "orange" : v === "Fin de semana" ? "blue" : v === "Vacaciones" ? "cyan" : "purple"}>{v}</Tag>
        : "—",
    },
    {
      title: "Horario", dataIndex: "horarioTexto", key: "horarioTexto", width: 150,
      render: (v) => <div style={{ whiteSpace: "pre-line" }}>{v || "—"}</div>,
    },
    {
      title: "Ent (H)", dataIndex: "horaEntrada", key: "horaEntrada",
      width: 90, align: "center",
      render: (v) => v || "—",
    },
    {
      title: "Sal (H)", dataIndex: "horaSalida", key: "horaSalida",
      width: 90, align: "center",
      render: (v) => v || "—",
    },
    {
      title: "Duración", dataIndex: "duracionMin", key: "duracionMin",
      width: 90, align: "center",
      render: (v) => this.fmtMin(v),
    },
    {
      title: "Entrada", key: "entrada", width: 100, align: "center",
      render: (_, r) => this.renderHoraConColor(r.entrada, r.entradaColor),
    },
    {
      title: "Salida", key: "salida", width: 100, align: "center",
      render: (_, r) => this.renderHoraConColor(r.salida, r.salidaColor),
    },
    {
      title: "Tiempo Total", dataIndex: "tiempoTotalMin", key: "tiempoTotalMin",
      width: 110, align: "center",
      render: (v) => this.fmtMin(v),
    },
    {
      title: "Jornada", dataIndex: "tiempoJornadaMin", key: "tiempoJornadaMin",
      width: 100, align: "center",
      render: (v) => this.fmtMin(v),
    },
    {
      title: "Sin Asignar", dataIndex: "sinAsignarMin", key: "sinAsignarMin",
      width: 110, align: "center",
      render: (v) => this.fmtMin(v),
    },
    {
      title: "Retardo", dataIndex: "retardoMin", key: "retardoMin",
      width: 90, align: "center",
      render: (v) => v
        ? <span style={{ color: COLOR_MAP.rojo, fontWeight: 600 }}>{this.fmtMin(v)}</span>
        : "—",
    },
    {
      title: "Sal. Temprana", dataIndex: "salidaTempranaMin", key: "salidaTempranaMin",
      width: 120, align: "center",
      render: (v) => v
        ? <span style={{ color: COLOR_MAP.rojo, fontWeight: 600 }}>{this.fmtMin(v)}</span>
        : "—",
    },
    {
      title: "Ausencia", dataIndex: "ausencia", key: "ausencia",
      width: 90, align: "center",
      render: (v, record) => record.excepcion === "Vacaciones"
        ? <Tag color="cyan">Vacaciones</Tag>
        : v ? <Tag color="red">Sí</Tag> : <Tag>No</Tag>,
      filters: [{ text: "Sí", value: true }, { text: "No", value: false }, { text: "Vacaciones", value: "vacaciones" }],
      onFilter: (value, record) => value === "vacaciones"
        ? record.excepcion === "Vacaciones"
        : !!record.ausencia === value,
    },
    {
      title: "Extra Normal", dataIndex: "extraNormalMin", key: "extraNormalMin",
      width: 110, align: "center",
      render: (v) => v
        ? <span style={{ color: COLOR_MAP.verde, fontWeight: 600 }}>{this.fmtMin(v)}</span>
        : "—",
    },
    {
      title: "Extra Fin Sem.", dataIndex: "extraFinSemanaMin", key: "extraFinSemanaMin",
      width: 120, align: "center",
      render: (v) => v
        ? <span style={{ color: COLOR_MAP.verde, fontWeight: 600 }}>{this.fmtMin(v)}</span>
        : "—",
    },
    {
      title: "Extra Festivo", dataIndex: "extraFestivoMin", key: "extraFestivoMin",
      width: 110, align: "center",
      render: (v) => v
        ? <span style={{ color: COLOR_MAP.verde, fontWeight: 600 }}>{this.fmtMin(v)}</span>
        : "—",
    },
    {
      title: "Extra Descanso", dataIndex: "extraDescansoMin", key: "extraDescansoMin",
      width: 130, align: "center",
      render: (v) => v
        ? <span style={{ color: COLOR_MAP.verde, fontWeight: 600 }}>{this.fmtMin(v)}</span>
        : "—",
    },
    {
      title: "Descanso Esperado", dataIndex: "descansoEsperadoMin", key: "descansoEsperadoMin",
      width: 140, align: "center",
      render: (v) => this.fmtMin(v),
    },
    {
      title: "Descanso Real", dataIndex: "descansoRealMin", key: "descansoRealMin",
      width: 130, align: "center",
      render: (v) => this.fmtMin(v),
    },
    {
      title: "Diferencia Descanso", dataIndex: "descansoDiferenciaMin", key: "descansoDiferenciaMin",
      width: 150, align: "center",
      render: (v) => {
        if (v === null || v === undefined) return "—";
        const n = Number(v);
        if (!n) return <span style={{ color: COLOR_MAP.gris }}>{this.fmtMin(v)}</span>;
        const color = n > 0 ? COLOR_MAP.rojo : COLOR_MAP.verde;
        return <span style={{ color, fontWeight: 600 }}>{n > 0 ? "+" : ""}{this.fmtMin(v)}</span>;
      },
    },
  ];

  // =========================
  // Render
  // =========================
  render() {
    const { loading, rango, departamentosIds, empleadosIds, todosEmpleados, rows, mostrarDiasSinHorario } = this.state;

    return (
      <>
        <PageHeader
          title="Total de Asistencia"
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
          extra={
            <>
              <Tooltip title="Consultar">
                <ButtonItem icon={<ReloadOutlined />} onClick={this.consultar} loading={loading}>
                  Consultar
                </ButtonItem>
              </Tooltip>
              <Tooltip title="Exportar a Excel">
                <ButtonItem type="default" icon={<DownloadOutlined />} onClick={this.exportExcel} disabled={!rows?.length}>
                  Excel
                </ButtonItem>
              </Tooltip>
              <Tooltip title="Exportar a PDF">
                <ButtonItem type="default" icon={<FilePdfOutlined />} onClick={this.exportPDF} disabled={!rows?.length}>
                  PDF
                </ButtonItem>
              </Tooltip>
            </>
          }
        />

        <Row gutter={[10, 10]}>
          {/* Filtros */}
          <Col xs={24}>
            <Card title="Filtros" size="small" style={{ borderRadius: 6, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
              <Row gutter={[16, 12]} align="bottom">
                <Col xs={24} md={7}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Departamento</div>
                  <SelectDepartamentosItem
                    empresaId={this.props.empresaId}
                    headersToken={this.props.headersToken}
                    sinBorde
                    value={departamentosIds}
                    onChange={this.onChangeDepartamento}
                    allowClear
                    width="100%"
                    placeholder="Todos los departamentos"
                    mode="multiple"
                    disabled={todosEmpleados}
                  />
                </Col>

                <Col xs={24} md={7}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: "#666" }}>Empleado</span>
                    <Checkbox
                      checked={todosEmpleados}
                      onChange={this.onChangeTodosEmpleados}
                      style={{ fontSize: 12 }}
                    >
                      Todos los empleados
                    </Checkbox>
                  </div>
                  <SelectEmpleadosItem
                    empresaId={this.props.empresaId}
                    headersToken={this.props.headersToken}
                    sinBorde
                    value={empleadosIds}
                    onChange={this.onChangeEmpleado}
                    allowClear
                    width="100%"
                    placeholder="Todos los empleados"
                    mode="multiple"
                    disabled={todosEmpleados}
                  />
                </Col>

                <Col xs={24} md={7}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Rango de fechas (máx. 31 días)</div>
                  <RangePicker
                    value={rango}
                    onChange={this.onChangeRango}
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    disabledDate={this.disabledDate}
                    allowClear={false}
                  />
                </Col>

                <Col xs={24} md={3}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Días sin horario</div>
                  <Switch
                    checked={mostrarDiasSinHorario}
                    onChange={this.onChangeMostrarSinHorario}
                    checkedChildren="Mostrar"
                    unCheckedChildren="Ocultar"
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Tabla */}
          <Col xs={24}>
            <Card
              title="Resultados"
              size="small"
              extra={
                loading
                  ? <Tag color="processing">Cargando...</Tag>
                  : <Tag>{rows?.length || 0} registros</Tag>
              }
              bodyStyle={{ padding: 0 }}
              style={{ minHeight: "80vh", borderRadius: 6, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}
            >
              <Spin spinning={loading}>
                {rows?.length
                  ? (
                    <Table
                      size="small"
                      rowKey={(r, idx) => `${r.nombre}-${r.fecha}-${r.horarioTexto}-${idx}`}
                      columns={this.getColumns()}
                      dataSource={rows}
                      loading={loading}
                      pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200] }}
                      scroll={{ x: 2820, y: "calc(100vh - 340px)" }}
                      sticky
                    />
                  )
                  : (
                    <div style={{ padding: 40 }}>
                      <Empty description="Selecciona filtros y consulta para ver resultados" />
                    </div>
                  )
                }
              </Spin>
            </Card>
          </Col>
        </Row>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresa: state.empresa.nombre,
});

export default connect(mapStateToProps)(TotalAsistencia);